import "./ResponsiveAppBar.css";
import React, { useState } from "react";
import { AppBar, Button } from "@mui/material";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import Container from "@mui/material/Container";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import { useNavigate } from "react-router-dom";
import useWindowDimensions from "@mirage/hooks/useWindowDimensions";
import MobileBottomAppBar from "./bottomResponsiveAppBar/MobileBottomAppBar";
import { UserService } from "@publicPortal/services/UserService";
import { PublicPortalTheme } from "@publicPortal/theme/MuiPublicPortal.module";
import { __version } from "../../../../version";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse, faRectangleHistory, faUser } from "@fortawesome/pro-solid-svg-icons";
import GradientBackground from "../gradientBackground/GradientBackground";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { IUser } from "@shared/mirage/core/contracts/IUser";
import { useRecoilValue } from "recoil";
import { userState } from "@mirage/store/UserStore";
import { PPConstants } from "@shared/publicportal/constants";
import useSessionStorage from "@mirage/hooks/useSessionStorage";

const pp = new PublicPortalRoutingService();
const pagesWithRoutes = [
    {
        page: "My Collection",
        endPoint: pp.CollectedDataRoute(),
    },
    {
        page: "Account",
        endPoint: pp.UserSettingsRoute(),
    },
    {
        page: "Logout",
        endPoint: pp.Root,
    },
];

const ResponsiveAppBar = () => {
    const userService = new UserService();
    const user = useRecoilValue(userState);
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const navigate = useNavigate();
    const { width } = useWindowDimensions();
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = async (path: string, page?: string) => {
        setAnchorElUser(null);
        if (page === "Logout") {
            await userService.logout();
            navigate(0);
        } else if (path) {
            navigate(path);
        }
    };

    const getMenuItems = (user: globalThis.Partial<IUser>) => {
        const menuItems = pagesWithRoutes.map((data) => (
            <MenuItem key={data.page} onClick={async () => await handleCloseUserMenu(data.endPoint, data.page)}>
                <Typography textAlign="center">{data.page}</Typography>
            </MenuItem>
        ));
        getUserMenuHeader(menuItems, user);
        appendVersionItem(menuItems);

        return menuItems;
    };

    const getMySearchButton = () => {
        if (!user || !user.id) {
            return null;
        }
        return (
            <Tooltip title={"My Search Collection"}>
                <IconButton onClick={() => navigate(pp.CollectedDataRoute())} className="icon-button">
                    <FontAwesomeIcon icon={faRectangleHistory} style={PublicPortalTheme.icons.sizeL} />
                </IconButton>
            </Tooltip>
        );
    };

    const getSignInOrUserAvatarButton = () => {
        if (!sessionValue.clientSettings?.ENABLE_LOGIN) {
            return <div></div>;
        }
        if (!user || !user.id) {
            return (
                <Button onClick={() => navigate(pp.LoginRoute())} variant="contained" className="sign-in-button">
                    Sign In
                </Button>
            );
        }
        return (
            <>
                <Tooltip title={getUserTooltipTitle(user)}>
                    <IconButton onClick={handleOpenUserMenu} className="icon-button">
                        <FontAwesomeIcon icon={faUser} style={PublicPortalTheme.icons.sizeL} />
                    </IconButton>
                </Tooltip>
                <Menu
                    className="menu-container"
                    id="menu-appBar"
                    MenuListProps={{ className: "menu-container__list-item" }}
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    keepMounted
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={() => handleCloseUserMenu("")}
                >
                    {getMenuItems(user)}
                </Menu>
            </>
        );
    };

    const getHomeButton = () => {
        return (
            <Tooltip title="Go to Search">
                <IconButton onClick={() => navigate(pp.Root)} className="icon-button">
                    <FontAwesomeIcon icon={faHouse} style={PublicPortalTheme.icons.sizeL} />
                </IconButton>
            </Tooltip>
        );
    };

    const getWaverleyLogo = () => {
        return (
            <Tooltip title="Go to website">
                <a aria-label="Go to website" href={sessionValue.clientSettings?.CLIENT_WEBSITE_URL} target="_blank" className={"logo"} rel="noreferrer">
                    {" "}
                </a>
            </Tooltip>
        );
    };

    const getMobileButtons = () => {
        return (
            <>
                {getMySearchButton()}
                {getHomeButton()}
                {getSignInOrUserAvatarButton()}
            </>
        );
    };

    const bigScreenRightSideButtons = () => {
        return (
            <>
                {getMySearchButton()}
                {getSignInOrUserAvatarButton()}
            </>
        );
    };

    return (
        <>
            <GradientBackground />
            <AppBar className="custom-app-bar" position="absolute">
                <Container maxWidth="xl">
                    <Toolbar disableGutters className="custom-app-bar__toolbar">
                        {width > PublicPortalTheme.breakpoints.values.md && <Box className="app-bar-icons-container">{getHomeButton()}</Box>}
                        <Box className="custom-app-bar__toolbar__search-container">{getWaverleyLogo()}</Box>
                        {width > PublicPortalTheme.breakpoints.values.md ? <Box className="app-bar-icons-container">{bigScreenRightSideButtons()}</Box> : null}
                    </Toolbar>
                </Container>
            </AppBar>
            {width <= PublicPortalTheme.breakpoints.values.md ? <MobileBottomAppBar button={getMobileButtons()} /> : null}
        </>
    );

    function getUserMenuHeader(menuItems: JSX.Element[], user: globalThis.Partial<IUser>) {
        menuItems.unshift(
            <li className="user-settings-menu-header-container" key={"user-settings-menu-header-container"}>
                <Typography variant="body1" fontWeight={600}>
                    {user?.first_name} {user?.last_name}
                </Typography>
            </li>
        );
    }
    function appendVersionItem(menuItems: JSX.Element[]) {
        menuItems.push(
            <li className="user-settings-menu-header-container" key={"user-settings-menu-header-container-version"}>
                <Typography variant="body1" fontWeight={100}>
                    Version: {__version}
                </Typography>
            </li>
        );
    }

    function getUserTooltipTitle(user: globalThis.Partial<IUser>) {
        if (user?.first_name && user?.last_name) {
            return (
                <div className="user-popper-container">
                    <span>
                        {user?.first_name} {user?.last_name}
                    </span>
                    <span>Account Settings</span>
                </div>
            );
        }
        return "Account Settings";
    }
};

export default ResponsiveAppBar;
